import type { ApiErrorBody } from '@shared/contracts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

let onPaymentRequired: ((reason: string) => void) | null = null;
/** The numbers wait for a plan (402 payment_required): the scope's owner's lapsed. */
export function setPaymentRequiredHandler(handler: (reason: string) => void) {
  onPaymentRequired = handler;
}

async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  raw?: { data: Blob; type: string },
): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: raw
        ? { 'Content-Type': raw.type }
        : body === undefined
          ? undefined
          : { 'Content-Type': 'application/json' },
      body: raw ? raw.data : body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    throw new ApiError(0, 'network', 'Can’t reach odo. Check your connection.');
  }
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (data as ApiErrorBody | null)?.error;
    if (res.status === 401 && e?.code === 'unauthorized') onUnauthorized?.();
    if (res.status === 402 && e?.code === 'payment_required') onPaymentRequired?.(e.fields?.reason ?? '');
    throw new ApiError(
      res.status,
      e?.code ?? 'error',
      e?.message ?? `Request failed (${res.status})`,
      e?.fields,
    );
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
  /** Raw body (e.g. an image), not JSON. */
  upload: <T>(path: string, data: Blob, type: string) =>
    request<T>('POST', path, undefined, undefined, { data, type }),
};
